/**
 * auto-artifacts: a sealed HTML page snapshot (a captured `open page.html`, or a Claude artifact)
 * decrypted on this device and shown in the sandboxed snapshot frame: scripts were removed and
 * assets inlined by the daemon, the frame adds an opaque origin and a no-network CSP, and the
 * decrypted HTML never becomes app-origin markup (S05). Opened only when the card is expanded.
 */
import { SandboxFrame } from '@viko/transcript-ui'
import { useEffect, useState } from 'react'
import type { BrowserArtifacts, EncryptedArtifactRef } from '../lib/encrypted-artifacts.js'
import { useOpenedArtifact } from './EncryptedArtifact.js'

export function EncryptedSnapshot({
  reader,
  reference,
  title,
}: {
  reader: Pick<BrowserArtifacts, 'open'>
  reference: EncryptedArtifactRef
  title: string
}) {
  const opened = useOpenedArtifact(reader, reference)
  const url = opened?.handle?.url
  const blob = opened?.handle?.blob
  const [html, setHtml] = useState<string | null>()
  useEffect(() => {
    if (!url) return
    let cancelled = false
    // In place (blob.text()): fetching a blob: URL is blocked by connect-src.
    ;(blob ? blob.text() : fetch(url).then((r) => r.text())).then(
      (t) => !cancelled && setHtml(t),
      () => !cancelled && setHtml(null),
    )
    return () => {
      cancelled = true
    }
  }, [url, blob])
  if (opened?.failed || html === null)
    return <span role="alert">Snapshot locked or unavailable</span>
  if (html === undefined) return <span>Loading snapshot…</span>
  return (
    <div className="vk-snapshot">
      <SandboxFrame html={html} title={`${title} (snapshot, scripts off)`} />
      <p className="vk-meta">Static snapshot: scripts removed, no network.</p>
    </div>
  )
}
